<template>
  <div class="p-[20px] border border-solid border-steam rounded-[8px] vs-con-loading__container" id="product-section-loading">
    <div class="flex items-center justify-between gap-1">
      <div class="flex gap-2">
        <shipblu-icon iconLibrary="feather" iconName="PackageIcon" iconClass="text-industrial-revolution text-[18px]"/>
        <p class="text-lg text-young-night">
          {{ $t('Product Details') }}
          <span class="sm:inline block text-[12px]">({{ $t('including tax if applicable') }})</span>
        </p>
      </div>
      <div v-if="$store.state.AppActiveUser.userRole === 'merchant' && merchantStatus === 'active' && common.defineOrderType(orderData) === 'delivery-orders' && ['created', 'pickup_requested', 'out_for_pickup', 'pickup_rescheduled', 'picked_up', 'in_transit', 'en_route'].includes(orderData.status)">
        <feather-icon icon="EditIcon" svgClasses="h-5 w-5" class="text-silver cursor-pointer flex" v-if="!editable" @click="editInformation()" />
      </div>
    </div>

    <div v-if="(orderData.status === 'delivered' && orderData.buy_and_try_product_in_order && orderData.buy_and_try_product_in_order.length > 0 && orderData.buy_and_try_product_in_order.filter(item => item.requested_quantity !== item.delivered_quantity).length > 0) || (orderData.return_items && orderData.return_items.length > 0)">
      <p class="mt-1 sm:text-base text-[12px] text-darkgray font-medium leading-tight">
        ({{ common.defineOrderType(orderData) === 'delivery-orders' ? $t('Try and Buy Return Order') : $t('Try and Buy Delivery Order') }}
        <router-link :to="openLinkedOrder()">
          <span class="text-primary underline font-semibold cursor-pointer">{{ common.defineOrderType(orderData) === 'delivery-orders' ? '2' + orderData.tracking_number + 'TB' : orderData.tracking_number.slice(1).split('TB')[0] }}</span>
        </router-link>)
      </p>
    </div>

    <div class="mt-6 flex flex-col gap-3">
      <div v-if="common.defineOrderType(orderData) === 'delivery-orders'">
        <div v-show="editable">
          <shipblu-input :labelPlaceholder="$t('Buy and Try shipping fees')" inputId="fees" name="shipping fees" type="text" v-validate="'required|decimal|min_value:0'" v-model="orderData.buy_and_try_shipping_fees" class="w-full" />
          <span class="text-danger text-[12px]" v-show="errors.has('shipping fees')">{{ errors.first('shipping fees') }}</span>
        </div>
        <p v-show="!editable" class="text-young-night leading-[20px] sm:text-base text-[12px]">{{ $t('Buy and Try shipping fees') }}: 
          <span class="text-industrial-revolution">{{ $t('EGP') }} {{ Number(orderData.buy_and_try_shipping_fees).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</span>
        </p>
      </div>

      <div 
        v-for="(items, groupKey, index) in (orderData.return_items || orderData.buy_and_try_product_in_order).reduce((groups, item) => {
          // If bundle_id exists, group by it; else use item.id for individual box
          const key = item.bundle_id ? item.bundle_id : item.id;
          (groups[key] = groups[key] || []).push(item);
          return groups;
        }, {})"
        :key="groupKey"
        class="p-3 flex flex-col gap-3 border border-solid border-steam rounded-md"
      >
        <div v-if="items[0].bundle_id && common.defineOrderType(orderData) === 'delivery-orders'">
          <div v-show="editable">
            <shipblu-input :disabled="!['created', 'pickup_requested', 'out_for_pickup', 'pickup_rescheduled'].includes(orderData.status)" @input="changeItemsRequestedQuantity(items[0].bundleQty, items)" :labelPlaceholder="$t('Bundle Qty')" :inputId="`bundleQty-${index}`" :name="`bundle quantity-${index}`" data-vv-as="bundle quantity" type="text" v-validate="'required|numeric|min_value:1'" v-model="items[0].bundleQty" class="w-full" />
            <span class="text-danger text-[12px]" v-show="errors.has(`bundle quantity-${index}`)">{{ errors.first(`bundle quantity-${index}`) }}</span>
          </div>
          <p v-show="!editable" class="text-young-night leading-[20px] sm:text-base text-[12px]"> {{ $t('Bundle Qty') }}:
            <span class="text-industrial-revolution">{{ items[0].bundle_id.split('QTY:')[1] }}</span>
          </p>
        </div>
        
        <div v-for="(item, index) in items" :key="index" class="flex gap-4 lg:items-center items-center">
          <img v-if="item.product_image_url" :src="item.product_image_url" alt="product image" class="lg:w-[64px] lg:h-[64px] w-[78px] h-[78px] flex-shrink-0 object-contain border border-solid border-steam p-1 rounded-md"/>
          <div v-else class="lg:w-[64px] lg:h-[64px] w-[78px] h-[78px] flex-shrink-0 bg-steam flex items-center justify-center rounded-md">
            <shipblu-icon iconLibrary="feather" iconName="ImageIcon" iconClass="text-white text-[24px]"/>
          </div>

          <div class="grid lg:grid-cols-2 gap-x-1">
            <p :class="`text-young-night font-medium ${!editable && 'lg:col-span-2 mb-1'}`">{{ item.product_name }}</p>

            <p class="text-young-night leading-[20px] sm:text-base text-[12px]">{{ $t('SKU') }}: 
              <span class="text-industrial-revolution">{{ item.product_sku }}</span>
            </p>

            <div>
              <div v-show="editable">
                <shipblu-input :labelPlaceholder="$t('Unit Price')" :inputId="`price-${item.id || index}`" :name="`unit price-${item.id || index}`" data-vv-as="unit price" type="text" v-validate="'required|decimal|min_value:0'" v-model="item.unit_price" class="w-full mt-4" />
                <span class="text-danger text-[12px]" v-show="errors.has(`unit price-${item.id || index}`)">{{ errors.first(`unit price-${item.id || index}`) }}</span>
              </div>
              <p v-show="!editable" class="text-young-night leading-[20px] sm:text-base text-[12px]">{{ $t('Unit Price') }}:
                <span class="text-industrial-revolution">{{ $t('EGP') }} {{ Number(item.unit_price).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</span>
              </p>
            </div>

            <div>
              <div v-show="editable">
                <shipblu-input :disabled="!['created', 'pickup_requested', 'out_for_pickup', 'pickup_rescheduled'].includes(orderData.status) || items[0].bundle_id !== ''" :labelPlaceholder="$t('Shipped Qty')" :inputId="`qty-${item.id || index}`" :name="`shipped quantity-${item.id || index}`" data-vv-as="shipped quantity" type="text" v-validate="'required|numeric|min_value:1'" v-model="item.requestedQty" class="w-full mt-4" />
                <span class="text-danger text-[12px]" v-show="errors.has(`shipped quantity-${item.id || index}`)">{{ errors.first(`shipped quantity-${item.id || index}`) }}</span>
              </div>
              <p v-show="!editable" class="text-young-night leading-[20px] sm:text-base text-[12px]">{{ $t('Shipped Qty') }}:
                <span class="text-industrial-revolution">{{ item.requested_quantity }}</span>
              </p>
            </div>

            <p v-if="orderData.status === 'delivered'" class="text-young-night leading-[20px] sm:text-base text-[12px]">{{ $t('Delivered Qty') }}:
              <span class="text-industrial-revolution">{{ item.delivered_quantity }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
    
    <div v-if="editable" class="grid grid-cols-2 gap-4 mt-6">
      <shipblu-button class="font-semibold" @click="updateProductDetails()" :textButton="$t('Save')"/>
      <shipblu-button class="font-semibold" @click="cancelEdit()" color="secondary" :textButton="$t('Cancel')"/>
    </div>
  </div>
</template>

<script>
import common from '@/assets/utils/common'
import { sendRequest } from '../../../http/axios/requestHelper'
import i18nData from '../../../i18n/i18nData'

export default {
  props: ['orderData', 'merchantStatus'],
  data () {
    return {
      common,
      editable: false
    }
  },
  watch: {
    'orderData' () {
      this.$validator.resume()
      this.editable = false
    }
  },
  methods: {
    updateProductDetails () {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.$vs.loading({
            background: this.backgroundLoading,
            color: this.colorLoading,
            container: '#product-section-loading',
            scale: 0.45
          })

          const productObj = {
            shipping_fees: Number(this.orderData.buy_and_try_shipping_fees),
            items: (this.orderData.buy_and_try_product_in_order || []).map(item => ({
              id: item.id,
              order: this.orderData.id,
              product_name: item.product_name,
              product_sku: item.product_sku,
              product_image_url: item.product_image_url,
              bundle_id: item.bundle_id,
              requested_quantity: Number(item.requestedQty),
              unit_price: Number(item.unit_price),
              vat_included: item.vat_included
            }))
          }

          sendRequest(true, false, this, `api/v1/delivery-orders/${this.orderData.id}/buy-and-try-products/`, 'patch', productObj, true,
            () => {
              this.$vs.notify({
                color: 'success',
                title: i18nData[this.$i18n.locale]['Success'],
                text: i18nData[this.$i18n.locale]['Product details'] + i18nData[this.$i18n.locale][' have been updated successfully!'],
                position: 'top-center'
              })
              this.cancelEdit()
              this.$vs.loading.close('#product-section-loading > .con-vs-loading')
            },
            (error) => {
              common.notifyResponseError(this, error)
              this.$vs.loading.close('#product-section-loading > .con-vs-loading')
              this.$emit('loadOrder')
            }
          )
        }
      })
    },
    changeItemsRequestedQuantity (bundleQty, items) {
      items.forEach(item => {
        item.requestedQty = Number(bundleQty) * (item.requested_quantity / Number(item.bundle_id.split('QTY:')[1]))
      })
    },
    editInformation () {
      this.editable = true
    },
    cancelEdit () {
      this.editable = false
      this.$validator.pause()
      this.$emit('loadOrder')
    },
    openLinkedOrder () {
      if (this.$store.state.AppActiveUser.userRole === 'head-of-fleet') {
        if (common.defineOrderType(this.orderData) === 'delivery-orders') {
          return `/${this.$i18n.locale}/team-leader/search?search=${`2${  this.orderData.tracking_number  }TB`}`
        } else {
          return `/${this.$i18n.locale}/team-leader/search?search=${this.orderData.tracking_number.slice(1).split('TB')[0]}`
        }
      } else {
        return common.defineOrderType(this.orderData) === 'delivery-orders' ? `/${this.$i18n.locale}/${this.$store.state.AppActiveUser.userRole}/orders/returns?search=${`2${  this.orderData.tracking_number  }TB`}` : `/${this.$i18n.locale}/${this.$store.state.AppActiveUser.userRole}/orders/delivery-orders?search=${this.orderData.tracking_number.slice(1).split('TB')[0]}` 
      }
    }
  }
}
</script>